import React, { useState, useCallback, useRef } from 'react';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  UploadSimple, FileText, FileCsv, X,
  SpinnerGap, Check, Trash, PencilSimple, Plus
} from '@phosphor-icons/react';

const ACCEPT_FILES = '.png,.jpg,.jpeg,.webp,.heic,.csv,.xlsx,.xls,.pdf';

const ENTITY_CONFIG = {
  inventory: {
    title: 'Import Inventory',
    subtitle: 'Upload a file to extract inventory items using AI',
    fields: ['name', 'category', 'quantity', 'unit', 'supplier', 'catalog_number', 'location'],
    nameField: 'name',
  },
  protocols: {
    title: 'Import Protocol',
    subtitle: 'Upload a protocol document to extract steps using AI',
    fields: ['title', 'description', 'steps', 'materials'],
    nameField: 'title',
    isObject: true,
  },
  experiments: {
    title: 'Import Experiments',
    subtitle: 'Upload a file to extract experiments using AI',
    fields: ['title', 'experiment_type', 'description', 'status', 'hypothesis', 'tags'],
    nameField: 'title',
  },
  samples: {
    title: 'Import Samples',
    subtitle: 'Upload a file to extract biological samples using AI',
    fields: ['name', 'sample_type', 'organism', 'storage_location', 'notes'],
    nameField: 'name',
  },
  plasmids: {
    title: 'Import Plasmids',
    subtitle: 'Upload a file to extract plasmid/vector data using AI',
    fields: ['name', 'vector_type', 'size_bp', 'resistance', 'promoter', 'insert_gene', 'source'],
    nameField: 'name',
  },
  strains: {
    title: 'Import Strains',
    subtitle: 'Upload a file to extract strain/cell line data using AI',
    fields: ['name', 'organism', 'strain_type', 'genotype', 'resistance', 'source'],
    nameField: 'name',
  },
  projects: {
    title: 'Import Projects',
    subtitle: 'Upload a file to extract project information using AI',
    fields: ['title', 'description', 'status', 'goals', 'tags'],
    nameField: 'title',
  },
};

const ImportModal = ({ type, onClose, onImportComplete }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editIdx, setEditIdx] = useState(-1);
  const fileInputRef = useRef(null);

  const config = ENTITY_CONFIG[type] || ENTITY_CONFIG.inventory;

  const handleFileSelect = useCallback((e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setExtractedData(null);
    if (f.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(ev.target.result);
      reader.readAsDataURL(f);
    } else {
      setPreview(null);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) {
      setFile(f);
      setExtractedData(null);
      if (f.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (ev) => setPreview(ev.target.result);
        reader.readAsDataURL(f);
      } else {
        setPreview(null);
      }
    }
  }, []);

  const processFile = async () => {
    if (!file) return;
    setProcessing(true);
    try {
      let result;
      if (type === 'inventory') {
        result = await api.importInventory(file);
        setExtractedData({ items: result.items, count: result.count });
      } else if (type === 'protocols') {
        result = await api.importProtocol(file);
        setExtractedData({ protocol: result.protocol, isProtocol: true });
      } else {
        result = await api.importGeneric(type, file);
        setExtractedData({ items: result.items, count: result.count });
      }
      toast.success(`Extracted ${config.isObject ? '1 protocol' : `${result.items?.length || result.count || 1} items`}`);
    } catch (err) {
      const msg = typeof err === 'string' ? err : err?.message || 'Failed to process file';
      toast.error(msg);
    }
    setProcessing(false);
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      if (type === 'inventory') {
        await api.confirmInventoryImport(extractedData.items);
      } else if (type === 'protocols') {
        await api.confirmProtocolImport(extractedData.protocol);
      } else {
        await api.confirmGenericImport(type, extractedData.items);
      }
      toast.success(`Imported successfully!`);
      onImportComplete?.();
      onClose();
    } catch (err) {
      const msg = typeof err === 'string' ? err : err?.message || 'Failed to save';
      toast.error(msg);
    }
    setSaving(false);
  };

  const removeItem = (idx) => {
    if (extractedData?.items) {
      const newItems = [...extractedData.items];
      newItems.splice(idx, 1);
      setExtractedData({ ...extractedData, items: newItems, count: newItems.length });
    }
  };

  const updateItem = (idx, field, value) => {
    if (extractedData?.items) {
      const newItems = [...extractedData.items];
      newItems[idx] = { ...newItems[idx], [field]: value };
      setExtractedData({ ...extractedData, items: newItems });
    }
  };

  const items = extractedData?.items || [];
  const isProtocol = extractedData?.isProtocol;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
      <div className="w-full max-w-2xl max-h-[85vh] flex flex-col rounded-xl overflow-hidden" style={{ background: '#1E293B', border: '1px solid rgba(148,163,184,0.15)' }} data-testid="import-modal">
        {/* Header */}
        <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(148,163,184,0.12)' }}>
          <div>
            <h2 className="text-base font-semibold" style={{ color: '#F8FAFC' }}>{config.title}</h2>
            <p className="text-xs mt-0.5" style={{ color: '#94A3B8' }}>{config.subtitle}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors" style={{ color: '#94A3B8' }} data-testid="import-close-btn">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {!extractedData ? (
            <div className="space-y-4">
              {/* Drop Zone */}
              <div
                className="rounded-xl p-8 text-center cursor-pointer transition-all"
                style={{
                  border: `2px dashed ${file ? '#818CF8' : 'rgba(148,163,184,0.2)'}`,
                  background: file ? 'rgba(99,102,241,0.06)' : 'rgba(15,23,42,0.4)',
                }}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                data-testid="import-dropzone"
              >
                <input ref={fileInputRef} type="file" accept={ACCEPT_FILES} onChange={handleFileSelect} className="hidden" />
                {preview ? (
                  <img src={preview} alt="Preview" className="max-h-40 mx-auto rounded-lg mb-3" />
                ) : (
                  <UploadSimple size={36} className="mx-auto mb-3" style={{ color: file ? '#818CF8' : '#64748B' }} />
                )}
                {file ? (
                  <div>
                    <p className="text-sm font-medium" style={{ color: '#F1F5F9' }}>{file.name}</p>
                    <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm font-medium" style={{ color: '#CBD5E1' }}>Drop file here or click to browse</p>
                    <p className="text-xs mt-1" style={{ color: '#64748B' }}>Supports images, CSV, Excel, PDF</p>
                  </div>
                )}
              </div>

              <Button onClick={processFile} disabled={!file || processing} className="btn-primary w-full" data-testid="import-process-btn">
                {processing ? <><SpinnerGap size={16} className="animate-spin mr-2" /> Processing with AI...</> : <><UploadSimple size={16} className="mr-2" /> Extract Data</>}
              </Button>
            </div>
          ) : isProtocol ? (
            /* Protocol review */
            <div className="space-y-3">
              <div className="p-3 rounded-lg" style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(148,163,184,0.1)' }}>
                <p className="text-sm font-semibold" style={{ color: '#F1F5F9' }}>{extractedData.protocol?.title}</p>
                <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>{extractedData.protocol?.description}</p>
                <p className="text-xs mt-1" style={{ color: '#64748B' }}>{extractedData.protocol?.steps?.length || 0} steps, {extractedData.protocol?.materials?.length || 0} materials</p>
              </div>
            </div>
          ) : (
            /* Items review */
            <div className="space-y-2">
              <p className="text-xs font-medium mb-2" style={{ color: '#94A3B8' }}>{items.length} items extracted — review and edit before importing</p>
              {items.map((item, idx) => (
                <div key={idx} className="p-3 rounded-lg flex items-start gap-3" style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(148,163,184,0.08)' }}>
                  <div className="flex-1 min-w-0">
                    {editIdx === idx ? (
                      <div className="space-y-2">
                        {config.fields.map(field => (
                          <div key={field}>
                            <label className="text-[10px] uppercase font-medium" style={{ color: '#64748B' }}>{field.replace(/_/g, ' ')}</label>
                            <Input
                              value={Array.isArray(item[field]) ? item[field].join(', ') : (item[field] || '')}
                              onChange={e => updateItem(idx, field, e.target.value)}
                              className="input-field text-xs mt-0.5"
                            />
                          </div>
                        ))}
                        <Button size="sm" onClick={() => setEditIdx(-1)} className="text-xs"><Check size={12} className="mr-1" /> Done</Button>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm font-medium truncate" style={{ color: '#F1F5F9' }}>{item[config.nameField] || 'Unnamed'}</p>
                        <p className="text-xs mt-0.5" style={{ color: '#94A3B8' }}>
                          {config.fields.filter(f => f !== config.nameField && item[f]).map(f => `${f.replace(/_/g, ' ')}: ${Array.isArray(item[f]) ? item[f].join(', ') : item[f]}`).join(' · ')}
                        </p>
                      </>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => setEditIdx(editIdx === idx ? -1 : idx)} className="p-1 rounded hover:bg-slate-700" style={{ color: '#94A3B8' }}>
                      <PencilSimple size={14} />
                    </button>
                    <button onClick={() => removeItem(idx)} className="p-1 rounded hover:bg-red-900/20" style={{ color: '#EF4444' }}>
                      <Trash size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {extractedData && (
          <div className="px-5 py-3 flex items-center justify-between" style={{ borderTop: '1px solid rgba(148,163,184,0.12)' }}>
            <Button variant="outline" className="btn-secondary text-xs" onClick={() => setExtractedData(null)}>
              Back
            </Button>
            <Button onClick={saveAll} disabled={saving || (!isProtocol && items.length === 0)} className="btn-primary text-xs" data-testid="import-save-btn">
              {saving ? <><SpinnerGap size={14} className="animate-spin mr-1" /> Saving...</> : <><Check size={14} className="mr-1" /> Import {isProtocol ? 'Protocol' : `${items.length} Items`}</>}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportModal;
